import * as React from "react";
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Card,
	Checkbox,
	Chip,
	CircularProgress,
	FormControlLabel,
	Grid,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { CheckCircleIcon } from "@phosphor-icons/react/dist/ssr/CheckCircle";
import { ClockIcon } from "@phosphor-icons/react/dist/ssr/Clock";
import { WarningIcon } from "@phosphor-icons/react/dist/ssr/Warning";
import { XCircleIcon } from "@phosphor-icons/react/dist/ssr/XCircle";
import { useNavigate } from "react-router-dom";

import {
	campaignRequestErrorMessage,
	useConfirmCampaignRequest,
	useQuoteCampaignRequest,
	useSubmitCampaignRequest,
} from "@/hooks/api/use-campaign-requests";
import { useAuth } from "@/components/core/auth-provider";

import { QuoteLinesTable } from "../components";
import {
	canConfirm,
	canTransition,
	formatCallingDays,
	formatDate,
	formatDateRange,
	formatDateTime,
	formatWallClock,
	isFrozen,
	marketClock,
	marketFlag,
	marketLabel,
	objectiveLabel,
	PRICING_PENDING_CLIENT_COPY,
	quoteAssumptions,
} from "../constants";
import { readAttestations } from "./attestations";
import { sameText } from "./dirty";
import { REQUESTS_PATH } from "./steps";
import type { StepProps } from "./wizard-shell";
import { agentBadge, StepHeading, useWizardDirty, useWizardLeaving, WizardFooter } from "./wizard-shell";

/**
 * Review the current server-priced quote before accepting it. Saved pricing edits trigger a
 * new quote on the server; clients can retry through /submit, while /quote stays admin-only.
 * A missing quote never exposes the signature controls.
 */

type ItemState = "settled" | "action" | "blocked" | "ours";

/**
 * One line of the readiness card.
 *
 * `label` is the thing itself in one to three words and never a sentence; `detail` is what is
 * true of it now. The pair is what lets the settled half be scanned in a glance and the
 * outstanding half be acted on — the old flat tick list made every row cost the same to read,
 * whether it was a filename we already have or a purchase order the account requires.
 */
interface ReadinessItem {
	key: string;
	label: string;
	detail: string;
	state: ItemState;
	/** Where the client fixes it. Set on their items only — ours have no button. */
	step?: number;
	action?: string;
}

const ITEM_ICONS: Record<ItemState, React.ComponentType<{ size?: number; weight?: "fill" }>> = {
	settled: CheckCircleIcon,
	action: WarningIcon,
	blocked: XCircleIcon,
	ours: ClockIcon,
};

const ITEM_COLORS: Record<ItemState, string> = {
	settled: "success.main",
	action: "warning.main",
	blocked: "error.main",
	ours: "info.main",
};

/** An item, or none, so the readiness list stays one expression instead of a run of pushes. */
function when(condition: boolean, item: ReadinessItem): ReadinessItem[] {
	return condition ? [item] : [];
}

/**
 * What a recipient template means, said in words.
 *
 * The template is stored as `{{contact__sales_rep}}` because that is what the mail service
 * resolves against each lead — but this is the last screen before a signature, and our merge
 * syntax printed on it is a leak, not a specification. Every phrase below is written for a
 * reader; a name we have no phrase for is described by the column it points at rather than
 * shown in braces.
 */
const RECIPIENT_PHRASES: Record<string, string> = {
	contact__sales_rep: "the sales rep on each lead",
	contact__sales_manager: "the sales manager",
	contact__name: "the person we spoke to",
	contact__email: "the contact's own address",
	contact__company: "the company on each lead",
};

const RECIPIENT_TOKEN = /^\{\{\s*([\w.]+)\s*\}\}$/;

function describeRecipient(part: string): string {
	const token = RECIPIENT_TOKEN.exec(part);
	// A typed-in address is already human, and paraphrasing it would hide who is being emailed.
	if (!token) return part;

	const name = token[1];
	const known = RECIPIENT_PHRASES[name];
	if (known) return known;

	// Every column of an uploaded list is addressable, so there is always a name we have no
	// phrase for. Its column is what the client recognises — the braces are what they do not.
	const column = name.includes("__") ? name.slice(name.indexOf("__") + 2) : name;
	return `the ${column.replaceAll("_", " ")} on each lead`;
}

/** A whole recipient field, read out: "the sales rep on each lead", or two of them joined. */
function describeRecipients(template: string | null | undefined): string | null {
	const parts = (template ?? "")
		.split(/[,;]/)
		.map((part) => part.trim())
		.filter((part) => part !== "");

	if (parts.length === 0) return null;

	const phrases = parts.map((part) => describeRecipient(part));
	if (phrases.length === 1) return phrases[0];

	return `${phrases.slice(0, -1).join(", ")} and ${phrases.at(-1)}`;
}

/** A group of readiness rows under its own heading. Outstanding work is never headingless. */
function ReadinessGroup({
	title,
	help,
	items,
	onFix,
}: {
	title: string;
	help?: string;
	items: ReadinessItem[];
	onFix?: (step: number) => void;
}): React.JSX.Element {
	return (
		<Stack spacing={1}>
			<Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
				<Typography variant="subtitle2">{title}</Typography>
				{help ? (
					<Typography variant="caption" color="text.secondary">
						{help}
					</Typography>
				) : null}
			</Stack>

			<Stack spacing={1}>
				{items.map((item) => {
					const Icon = ITEM_ICONS[item.state];
					// Read out of the item rather than off it inside the handler: a property
					// narrowed by `!== undefined` is wide again inside a closure.
					const step = item.step;

					return (
						<Stack
							key={item.key}
							direction="row"
							spacing={1.5}
							sx={{
								alignItems: "center",
								border: "1px solid var(--mui-palette-divider)",
								borderLeft: "3px solid",
								borderLeftColor: ITEM_COLORS[item.state],
								borderRadius: 1,
								px: 1.5,
								py: 1,
							}}
						>
							<Box sx={{ color: ITEM_COLORS[item.state], display: "flex" }}>
								<Icon size={18} weight="fill" />
							</Box>

							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Typography variant="body2" sx={{ fontWeight: 600 }}>
									{item.label}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{item.detail}
								</Typography>
							</Box>

							{onFix && step !== undefined && item.action ? (
								<Button size="small" onClick={() => onFix(step)}>
									{item.action}
								</Button>
							) : null}
						</Stack>
					);
				})}
			</Stack>
		</Stack>
	);
}

export function StepReview({ request, goTo, readOnly }: StepProps): React.JSX.Element {
	const navigate = useNavigate();

	const { user } = useAuth();
	const isSuperAdmin = Boolean(user?.isSuperAdmin);

	const submitMutation = useSubmitCampaignRequest();
	const quoteMutation = useQuoteCampaignRequest();
	const confirmMutation = useConfirmCampaignRequest();

	const [confirmed, setConfirmed] = React.useState(false);
	const [name, setName] = React.useState(request.confirmedByName ?? "");
	const [role, setRole] = React.useState(request.confirmedByRole ?? "");
	const [busy, setBusy] = React.useState(false);
	const [quoteFailed, setQuoteFailed] = React.useState(false);
	const [confirmError, setConfirmError] = React.useState<string | null>(null);

	const quote = request.currentQuote;
	const currency = quote?.currency ?? "SGD";
	const assumptions = quoteAssumptions(quote?.assumptions);

	// Read off the request, never held here: the dates behind these two are the API's, and a tick
	// cached in this component would survive a reload the record did not.
	const attestations = readAttestations(request);

	const callFlows = request.documents.filter(
		(document) => document.kind === "call_flow" || document.kind === "revised_call_flow"
	);
	const leadLists = request.documents.filter((document) => document.kind === "lead_list");
	const hasSow = Boolean(request.sowReference) || request.documents.some((document) => document.kind === "sow");
	const hasPo = Boolean(request.poReference) || request.documents.some((document) => document.kind === "po");
	const needsFlow = request.agents.some((agent) => agent.kind === "agent_setup" || agent.kind === "agent_revision");
	const conversions = request.outcomes.filter((outcome) => outcome.isConversion).length;
	const maxAttempts = request.marketSchedules
		.map((schedule) => schedule.maxAttemptsPerPerson)
		.filter((value): value is number => value !== null);

	/**
	 * Whether the Number column has anything in it.
	 *
	 * Local numbers are rented and assigned during preparation, so on almost every first request
	 * this column is six rows of "—" beside five columns that say something. A column that is
	 * entirely em-dashes is a column that is not carrying its own width; where it is empty the
	 * fact moves into the readiness card as one line, filed under us.
	 */
	const showNumbers = request.marketSchedules.some((schedule) => schedule.originNumber ?? schedule.originNumberId);

	// Read out rather than printed: this is the last screen before a signature, and
	// `{{contact__sales_rep}}` on it says nothing about who gets the email.
	const emailTo = describeRecipients(request.notification?.toTemplate);
	const emailBcc = describeRecipients(request.notification?.bccTemplate);

	/**
	 * Everything a quote is built from is present.
	 *
	 * `priceRequest` refuses a request with no agents — the fixed side of the quote is the step 2
	 * choices and there are none — and a quote with no markets carries no number rental and no
	 * carrier line, which is not an offer anybody should sign. Both are checked here rather than
	 * discovered by the API, because a submit that cannot be priced leaves the request in
	 * `submitted` until the missing agent or market settings are supplied.
	 */
	const schedulesComplete =
		request.markets.length > 0 &&
		request.markets.every((market) => request.marketSchedules.some((schedule) => schedule.market === market));
	const priceable = request.agents.length > 0 && schedulesComplete;

	type Phase = "gated" | "pricing" | "repricing" | "failed" | "confirm" | "settled" | "closed";

	const phase: Phase =
		request.state === "cancelled"
			? "closed"
			: quote && canConfirm(request.state)
				? "confirm"
				: isFrozen(request.state)
					? "settled"
					: // The earlier quote was withdrawn; show the server retry path.
						request.state === "changes_requested"
						? "repricing"
						: canTransition(request.state, "submitted")
							? priceable
								? quoteFailed
									? "failed"
									: "pricing"
								: "gated"
							: "pricing";

	/**
	 * The quote is being built right now.
	 *
	 * True while the submit is in flight, and still true after it resolves until the detail query
	 * catches up — otherwise the half-second between the mutation settling and the refetched
	 * request arriving renders "we're finalising your pricing" over a request that was priced
	 * successfully a moment ago.
	 */
	const assembling =
		submitMutation.isPending || (submitMutation.isSuccess && canTransition(request.state, "submitted"));

	/**
	 * A re-price is in flight, by the same reasoning and with the same tail: true until the quote
	 * it produced is on the request, so the moment between `POST /quote` resolving and the detail
	 * query catching up does not flash "not re-priced yet" over a request that just was.
	 */
	const repricing = quoteMutation.isPending || (quoteMutation.isSuccess && !quote);

	/**
	 * The signature is typed here and written by the confirm, so leaving with it half typed loses
	 * it. Two fields is not much, but "I clicked the rail and my name went" is the same complaint
	 * at any size.
	 */
	const leaving = useWizardLeaving();
	useWizardDirty(!sameText(name, request.confirmedByName) || !sameText(role, request.confirmedByRole));

	/**
	 * Ask for the price. Nothing is authorised by it.
	 *
	 * `POST /submit` is the client-callable door into pricing: the API quotes the request on the
	 * way through, because `POST /quote` grants no client role and a wizard that called it would
	 * 403 for every owner, manager and admin on the account. The attestations are not sent — they
	 * are stamped by their own endpoint the moment they are ticked on step 4, and `confirm()`
	 * records whichever is still unstamped.
	 *
	 * The quote arrives on the next render, through the detail query the wizard already holds.
	 */
	const runQuote = React.useCallback(async (): Promise<void> => {
		setQuoteFailed(false);
		setConfirmError(null);

		try {
			await submitMutation.mutateAsync({ id: request.id });
		} catch (error: any) {
			setQuoteFailed(true);
			setConfirmError(campaignRequestErrorMessage(error, "This request could not be sent for pricing."));
		}
	}, [request.id, submitMutation]);

	/**
	 * Price it on arrival, once.
	 *
	 * The client should land on a quote assembling and then a quote, not on a button whose purpose
	 * they have to infer. Guarded by a ref against the request id rather than by a dependency
	 * list: `submitMutation` is a new object on every render, and re-submitting a request would
	 * be a second quote version for nothing.
	 *
	 * Initial pricing runs from draft. Withdrawn quotes use the repricing effect below.
	 */
	const autoQuoted = React.useRef<string | null>(null);

	React.useEffect(() => {
		if (readOnly || !priceable) return;
		if (!canTransition(request.state, "submitted")) return;
		if (autoQuoted.current === request.id) return;

		autoQuoted.current = request.id;
		void runQuote();
	}, [priceable, readOnly, request.id, request.state, runQuote]);

	/**
	 * Accept the quote that is on the screen.
	 *
	 * Only reachable once `currentQuote` is rendered above, so what is being authorised is what
	 * was read. The two attestations ride along because `/confirm` records whichever is still
	 * unstamped, and re-ticking one already recorded cannot move its date.
	 */
	const handleConfirm = async (): Promise<void> => {
		setBusy(true);
		setConfirmError(null);

		try {
			await confirmMutation.mutateAsync({
				id: request.id,
				data: {
					quoteId: quote!.id,
					name: name.trim(),
					role: role.trim(),
					leadConsentConfirmed: attestations.leadConsentConfirmed,
					estimatesAcknowledged: attestations.estimatesAcknowledged,
				},
			});

			// The signature this step reports dirty on is only written by the confirm that just
			// landed, and the detail query has not caught up yet — so without this the client gets
			// "discard your changes?" over the name they just signed with.
			leaving();
			navigate(`${REQUESTS_PATH}/${request.id}`);
		} catch (error: any) {
			setConfirmError(campaignRequestErrorMessage(error, "This request could not be confirmed."));
		} finally {
			setBusy(false);
		}
	};

	/** Admins can inspect pricing errors directly; clients retry server pricing through submit. */
	const priceNow = React.useCallback(async (): Promise<void> => {
		setBusy(true);
		setConfirmError(null);

		try {
			await (isSuperAdmin ? quoteMutation.mutateAsync({ id: request.id }) : submitMutation.mutateAsync({ id: request.id }));
		} catch (error: any) {
			setConfirmError(campaignRequestErrorMessage(error, "This request could not be priced."));
		} finally {
			setBusy(false);
		}
	}, [isSuperAdmin, quoteMutation, request.id, submitMutation]);

	/** A complete request with no current offer can retry pricing without changing its rates. */
	const canPrice =
		!readOnly &&
		!quote &&
		priceable &&
		!assembling &&
		!repricing &&
		(request.state === "submitted" || request.state === "changes_requested");

	/** Retry a withdrawn quote once on arrival. Manual retries remain available if rates need repair. */
	const autoRepriced = React.useRef<string | null>(null);
	const [autoRepriceTried, setAutoRepriceTried] = React.useState(false);

	React.useEffect(() => {
		if (request.state !== "changes_requested") return;
		if (!canPrice) return;
		if (autoRepriced.current === request.id) return;

		autoRepriced.current = request.id;
		setAutoRepriceTried(true);
		void priceNow();
	}, [canPrice, priceNow, request.id, request.state]);

	/**
	 * The frame between paint and the effect firing, which the screen has to spend saying the same
	 * thing as the frame after it. Without this the operator sees "this has not been re-priced" and
	 * a button for one frame, then a spinner — the smaller version of the same lie.
	 */
	const autoRepricePending = phase === "repricing" && canPrice && !autoRepriceTried;

	/** In flight, or about to be. Everything on the screen reads this rather than the mutation. */
	const pricingNow = repricing || autoRepricePending;

	/** The button exists only where a human still has to press something. */
	const priceAction = canPrice && !autoRepricePending;

	// ------------------------------------------------------------------ readiness

	/**
	 * Every row of the readiness card, in reading order, as one expression.
	 *
	 * Two of them are gates rather than notes: a request with no agents cannot be priced at all
	 * (the fixed side of a quote *is* the step 2 choices), and one with no markets prices to an
	 * offer carrying no number rental and no carrier line. Both are the client's, and both have a
	 * button, because the alternative is a submit that lands in `submitted` and waits on us for
	 * something only they can supply.
	 */
	const items: ReadinessItem[] = [
		...when(request.agents.length === 0, {
			key: "agents",
			label: "Agents",
			detail: "None chosen — your quote is built from these",
			state: "blocked",
			step: 2,
			action: "Choose agents",
		}),

		...when(!schedulesComplete, {
			key: "markets",
			label: "Markets",
			detail: "Save a schedule for every selected market",
			state: "blocked",
			step: 3,
			action: "Finish schedules",
		}),

		needsFlow
			? callFlows.length > 0
				? {
						key: "call-flow",
						label: "Call flow",
						detail: callFlows.map((document) => document.originalFilename).join(", "),
						state: "settled",
					}
				: {
						key: "call-flow",
						label: "Call flow",
						detail: "Not attached — we can quote and confirm without it, but not build",
						state: "action",
						step: 2,
						action: "Attach it",
					}
			: {
					key: "call-flow",
					label: "Call flow",
					detail: "Not needed — every agent on this request already exists",
					state: "settled",
				},

		leadLists.length > 0
			? {
					key: "leads",
					label: "Lead list",
					detail: `${leadLists.length} file${leadLists.length === 1 ? "" : "s"} uploaded`,
					state: "settled",
				}
			: {
					key: "leads",
					label: "Lead list",
					detail: request.leadsDueDate
						? `To follow — promised ${formatDate(request.leadsDueDate)}`
						: "Not uploaded, and no date promised",
					state: "action",
					step: 4,
					action: "Add leads",
				},

		request.leadConsentConfirmedAt
			? {
					key: "consent",
					label: "Lead consent",
					detail: `Confirmed ${formatDate(request.leadConsentConfirmedAt)}`,
					state: "settled",
				}
			: {
					key: "consent",
					label: "Lead consent",
					detail: "Not yet confirmed",
					state: "action",
					step: 4,
					action: "Confirm it",
				},

		request.estimatesAcknowledgedAt
			? {
					key: "estimates",
					label: "Estimates",
					detail: `Acknowledged ${formatDate(request.estimatesAcknowledgedAt)} — illustrative only`,
					state: "settled",
				}
			: {
					key: "estimates",
					label: "Estimates",
					detail: "Not yet acknowledged as illustrative",
					state: "action",
					step: 4,
					action: "Acknowledge",
				},

		hasSow
			? { key: "sow", label: "Statement of work", detail: request.sowReference ?? "Attached", state: "settled" }
			: {
					key: "sow",
					label: "Statement of work",
					detail: request.sowRequired ? "Required on this account" : "Not attached — you can add this later",
					state: request.sowRequired ? "blocked" : "action",
					step: 7,
					action: "Add it",
				},

		hasPo
			? { key: "po", label: "Purchase order", detail: request.poReference ?? "Attached", state: "settled" }
			: {
					key: "po",
					label: "Purchase order",
					detail: request.poRequired ? "Required on this account" : "Not attached — you can add this later",
					state: request.poRequired ? "blocked" : "action",
					step: 7,
					action: "Add it",
				},

		request.outcomes.length > 0
			? {
					key: "outcomes",
					label: "Outcomes",
					detail: `${request.outcomes.length} outcomes, ${conversions} count as conversions · ${request.captureFields.length} field${request.captureFields.length === 1 ? "" : "s"} captured`,
					state: "settled",
				}
			: {
					key: "outcomes",
					label: "Outcomes",
					detail: "None set — nothing would count as a conversion",
					state: "action",
					step: 5,
					action: "Add outcomes",
				},

		emailTo
			? {
					key: "email",
					label: "Conversion email",
					detail: `To ${emailTo}${emailBcc ? `, BCC ${emailBcc}` : ""}`,
					state: "settled",
				}
			: {
					key: "email",
					label: "Conversion email",
					detail: "Nobody is emailed — we still record every outcome",
					state: "action",
					step: 5,
					action: "Set it up",
				},

		maxAttempts.length > 0
			? {
					key: "attempts",
					label: "Attempts",
					detail: `Max ${Math.max(...maxAttempts)} per person · do-not-call register applied`,
					state: "settled",
				}
			: {
					key: "attempts",
					label: "Attempts",
					detail: "No limit per person · do-not-call register applied",
					state: "action",
					step: 3,
					action: "Set a limit",
				},

		// Ours, and filed as ours. Numbers are rented and assigned during preparation, so an empty
		// Number column is us owing them a number rather than them owing us anything.
		...when(request.marketSchedules.length > 0 && !showNumbers, {
			key: "numbers",
			label: "Local numbers",
			detail: "We rent and assign one per market before dialling",
			state: "ours",
		}),

		// The one row that says whose court the price is in. Every reason we cannot produce a quote
		// is ours — a placeholder rate, a gap in the price book, a book that disagrees with itself
		// about currency — and none of them is anything the client can act on.
		...when(!quote && (phase === "pricing" || phase === "repricing"), {
			key: "quote",
			label: "Your quote",
			detail: pricingNow
				? "Being re-priced now"
				: assembling
					? "Being built now"
					: phase === "repricing"
						? isSuperAdmin
							? "Withdrawn by the edit, and not re-priced yet"
							: "Withdrawn when the request changed — we price the new one"
						: "Being finalised by us",
			state: "ours",
		}),
	];

	const yours = items.filter((item) => item.state === "blocked" || item.state === "action");
	const ours = items.filter((item) => item.state === "ours");
	const settled = items.filter((item) => item.state === "settled");
	const outstanding = yours.length + ours.length;
	const blocked = items.some((item) => item.state === "blocked");

	const attested = attestations.leadConsentConfirmed && attestations.estimatesAcknowledged;
	const canSign = !blocked && confirmed && attested && name.trim() !== "" && role.trim() !== "";

	/**
	 * The primary says what pressing it does, and there is no second button behind it.
	 *
	 * "Price this request" used to sit in the secondary slot beside a disabled primary reading
	 * "Waiting for your quote" — a screen waiting for the person standing in front of it. Where
	 * this browser can price, pricing *is* the action, so it is the primary; where it cannot, the
	 * disabled label names who is being waited on.
	 */
	const continueLabel =
		phase === "confirm"
			? "I confirm this quote"
			: phase === "failed"
				? "Try again"
				: phase === "settled"
					? "Already confirmed"
					: phase === "closed"
						? "Cancelled"
						: pricingNow
							? "Re-pricing this request"
							: assembling
								? "Preparing your quote"
								: priceAction
									? isSuperAdmin
										? "Price this request"
										: "Retry pricing"
									: phase === "repricing"
										? "Waiting for your new quote"
										: phase === "pricing"
											? "Waiting for your quote"
											: "I confirm this quote";

	return (
		<Stack spacing={3}>
			<StepHeading title={request.title} help="One screen, everything, then a signature.">
				<Stack spacing={1} sx={{ mt: 1 }}>
					<Typography variant="body2" color="text.secondary">
						{[
							request.clientReference,
							objectiveLabel(request.objective),
							`${request.markets.length} market${request.markets.length === 1 ? "" : "s"}`,
							`${request.agents.length} agent${request.agents.length === 1 ? "" : "s"}`,
						]
							.filter(Boolean)
							.join(" · ")}
					</Typography>
				</Stack>
			</StepHeading>

			<Card variant="outlined" sx={{ overflowX: "auto" }}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={{ fontWeight: 600 }}>Market</TableCell>
							<TableCell sx={{ fontWeight: 600 }}>Agent</TableCell>
							{showNumbers ? <TableCell sx={{ fontWeight: 600 }}>Number</TableCell> : null}
							<TableCell sx={{ fontWeight: 600 }}>Window</TableCell>
							<TableCell sx={{ fontWeight: 600 }}>Days</TableCell>
							<TableCell sx={{ fontWeight: 600 }}>Dates</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{request.marketSchedules.map((schedule) => {
							const agent = request.agents.find((candidate) => candidate.id === schedule.agentId);
							const clock = marketClock(schedule.market);

							return (
								<TableRow key={schedule.id}>
									<TableCell>
										{marketFlag(schedule.market)} {marketLabel(schedule.market)}
									</TableCell>
									{/* "English", or "English · Poly EN" where the agent was actually renamed. */}
									<TableCell>{agent ? agentBadge(agent.label, agent.language) : "—"}</TableCell>
									{/* Published on the schedule row itself. This used to be looked up through
									    `GET /phone-numbers`, which is owner-only — so the confirmation screen
									    threw a "Forbidden" toast for every admin and manager on the account, and
									    fell back to printing `#41` past the first hundred numbers even for an
									    owner. */}
									{showNumbers ? <TableCell>{schedule.originNumber ?? "Not yet assigned"}</TableCell> : null}
									<TableCell>
										{formatWallClock(schedule.callingWindowStart)} – {formatWallClock(schedule.callingWindowEnd)}
										{clock ? ` ${clock}` : ""}
									</TableCell>
									<TableCell>{formatCallingDays(schedule.callingDays)}</TableCell>
									<TableCell>{formatDateRange(schedule.dialStartDate, schedule.dialEndDate)}</TableCell>
								</TableRow>
							);
						})}

						{request.marketSchedules.length === 0 ? (
							<TableRow>
								<TableCell colSpan={showNumbers ? 6 : 5}>
									<Typography variant="body2" color="text.secondary">
										No markets scheduled yet.
									</Typography>
								</TableCell>
							</TableRow>
						) : null}
					</TableBody>
				</Table>
			</Card>

			{/*
			 * Readiness, in three named groups rather than one flat column of ticks.
			 *
			 * The old list gave a filename we already have and a purchase order the account
			 * requires the same weight, the same icon size and the same position, so the one row
			 * that needed an answer had to be found by reading all eight. Split by who owes what,
			 * outstanding first, and every one of the client's rows carries the button that fixes
			 * it — an amber row with nowhere to go is a complaint, not a task.
			 */}
			<Card variant="outlined">
				<Stack
					direction="row"
					spacing={2}
					sx={{
						alignItems: "center",
						borderBottom: "1px solid var(--mui-palette-divider)",
						justifyContent: "space-between",
						px: 2,
						py: 1.5,
					}}
				>
					<Typography variant="subtitle1">Readiness</Typography>
					<Chip
						size="small"
						variant="outlined"
						color={outstanding === 0 ? "success" : blocked ? "error" : "warning"}
						label={outstanding === 0 ? "All settled" : `${outstanding} outstanding`}
					/>
				</Stack>

				<Stack spacing={2.5} sx={{ p: 2 }}>
					{/* No help line: a red row already says "Required on this account" and an amber one
					    already says "you can add this later", so a qualifier over the group would be
					    true of half of it. */}
					{yours.length > 0 ? (
						<ReadinessGroup title="With you" items={yours} onFix={readOnly ? undefined : goTo} />
					) : null}

					{/* "Nothing needed from you" is true of a client and false of the operator reading
					    the same card — the row underneath it is the one thing they are being asked
					    to do. Said to whoever it is true of. */}
					{ours.length > 0 ? (
						<ReadinessGroup
							title="With us"
							help={isSuperAdmin ? "Ours to clear" : "Nothing needed from you"}
							items={ours}
						/>
					) : null}

					{settled.length > 0 ? (
						<Stack spacing={1}>
							{/* Named only when there is something else beside it; on a request with nothing
							    outstanding the chip above already says "All settled". */}
							{outstanding > 0 ? <Typography variant="subtitle2">Settled</Typography> : null}

							<Grid container spacing={1}>
								{settled.map((item) => (
									<Grid key={item.key} size={{ xs: 12, md: 6 }}>
										<Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
											<Box sx={{ color: "success.main", display: "flex", mt: 0.25 }}>
												<CheckCircleIcon size={16} weight="fill" />
											</Box>
											<Typography variant="body2" color="text.secondary">
												<Box component="span" sx={{ color: "text.primary" }}>
													{item.label}
												</Box>{" "}
												· {item.detail}
											</Typography>
										</Stack>
									</Grid>
								))}
							</Grid>
						</Stack>
					) : null}
				</Stack>
			</Card>

			<Card variant="outlined" sx={{ p: 2 }}>
				<Stack spacing={2}>
					<Stack direction="row" spacing={1} sx={{ alignItems: "baseline", justifyContent: "space-between" }}>
						{/* Version only once there is more than one: "version 1" on a first quote is a
						    number nobody is comparing anything to. */}
						<Typography variant="subtitle1">
							{quote && quote.version > 1 ? `Your quote · version ${quote.version}` : "Your quote"}
						</Typography>
						{/* A promise to them and a rule for us, so it is stated on the quote — and only
						    on the quote. Over an empty card it is a term for something that does not
						    exist yet. */}
						{quote ? (
							<Typography variant="caption" color="text.secondary">
								Rates fixed at confirmation
							</Typography>
						) : null}
					</Stack>

					{/* In full, and above the signature: every line, and the assumptions the estimate was
					    built from. This is the number being authorised, so it is the number on the
					    screen — not a two-line summary of it. */}
					{quote ? (
						<React.Fragment>
							<QuoteLinesTable
								lines={quote.lines}
								currency={currency}
								fixedTotal={quote.fixedTotal}
								variableLow={quote.variableLow}
								variableHigh={quote.variableHigh}
							/>

							{assumptions.length > 0 ? (
								<Box>
									<Typography variant="subtitle2" gutterBottom>
										What the Estimate Assumes
									</Typography>
									<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
										{assumptions.map((assumption) => (
											<Chip
												key={assumption.key}
												label={`${assumption.label}: ${assumption.value}`}
												size="small"
												variant="outlined"
											/>
										))}
									</Box>
								</Box>
							) : null}
						</React.Fragment>
					) : assembling || pricingNow ? (
						/* The quote assembling, rather than a spinner over an empty card: the client
						   arrived expecting a price and this is the shape of one arriving. */
						<Stack spacing={1.5}>
							<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
								<CircularProgress size={16} />
								<Typography variant="body2" color="text.secondary">
									{pricingNow
										? "Re-pricing this campaign against your account rates."
										: "Pricing this campaign against your account rates."}
								</Typography>
							</Stack>
							<Skeleton variant="rounded" height={16} width="62%" />
							<Skeleton variant="rounded" height={16} width="48%" />
							<Skeleton variant="rounded" height={16} width="55%" />
							<Skeleton variant="rounded" height={22} width="34%" />
						</Stack>
					) : phase === "gated" ? (
						<Alert severity="info">
							<AlertTitle>Nothing to price yet</AlertTitle>
							Your quote is built from the agents and markets on this request. Finish the items above and it prices
							itself.
						</Alert>
					) : phase === "failed" ? (
						<Alert severity="warning">
							<AlertTitle>We could not price this yet</AlertTitle>
							Your request could not be priced. Try again with the button below.
						</Alert>
					) : phase === "closed" ? (
						<Alert severity="info">This request was cancelled, so there is nothing to price.</Alert>
					) : phase === "settled" ? (
						<Alert severity="info">The terms on this request are settled. Ask for changes on the request itself.</Alert>
					) : phase === "repricing" ? (
						isSuperAdmin ? (
							<Alert severity="warning">
								<AlertTitle>The edit withdrew the last quote</AlertTitle>
								{priceAction
									? "This request has not been re-priced. Press Price this request below; the new version supersedes the withdrawn one."
									: "Finish the outstanding items above, then retry pricing."}
							</Alert>
						) : (
							<Alert severity="info">
								<AlertTitle>Your changes withdrew the earlier quote</AlertTitle>A quote prices one exact configuration,
								so the version you saw no longer describes this request.{" "}
								{priceable
									? "We could not produce a replacement yet. You can retry pricing below; AdaptiveX will resolve any missing rates."
									: "Complete the agents and market schedules above to receive an updated quote."}
							</Alert>
						)
					) : priceAction && isSuperAdmin ? (
						/*
						 * `submitted` with no quote: `autoQuote()` swallowed a refusal on the way
						 * through the submit and left the request here for us. The client's copy
						 * below is right for the client and wrong for the operator — "nothing more
						 * is needed from you" printed above a button only they can press is the
						 * same lie the re-price screen used to tell.
						 */
						<Alert severity="warning">
							<AlertTitle>The automatic pass could not price this</AlertTitle>
							It was quoted on submit and refused — the reason is in the log, and pressing Price this request below
							repeats it here in full.
						</Alert>
					) : (
						<Alert severity="info">
							<AlertTitle>We&rsquo;re finalising your pricing</AlertTitle>
							{PRICING_PENDING_CLIENT_COPY}
						</Alert>
					)}
				</Stack>
			</Card>

			{confirmError ? (
				<Alert severity="error">
					<AlertTitle>
						{phase === "confirm" ? "We could not confirm this request" : "We could not price this request"}
					</AlertTitle>
					{confirmError}
				</Alert>
			) : null}

			{/* The signature, and nothing before the quote it signs. A tick offered next to an empty
			    quote card is a tick against nothing — which is exactly what it was. The two
			    attestations are not repeated here: they are made and stamped on step 4 beside the
			    leads they describe, and the readiness card above says whether they are on the
			    record and links back where they are not. A disabled tick already on the record is
			    a control with one answer, which is furniture. */}
			{phase === "confirm" ? (
				<Card variant="outlined" sx={{ p: 2 }}>
					<Stack spacing={2}>
						<FormControlLabel
							control={<Checkbox checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />}
							label={
								<Box>
									<Typography variant="body2" sx={{ fontWeight: 600 }}>
										I confirm this quote and authorise this campaign to run.
									</Typography>
									<Typography variant="caption" color="text.secondary">
										Setup charges are fixed. Call time is billed per second from connection at the rate above, and
										carrier charges are passed through at cost. We will tell you if it looks like going over the
										estimate.
									</Typography>
								</Box>
							}
						/>

						<Grid container spacing={2}>
							<Grid size={{ xs: 12, md: 4 }}>
								<TextField
									fullWidth
									required
									label="Name"
									value={name}
									onChange={(event) => setName(event.target.value)}
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 4 }}>
								<TextField
									fullWidth
									required
									label="Role"
									value={role}
									onChange={(event) => setRole(event.target.value)}
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 4 }}>
								<TextField
									fullWidth
									disabled
									label="Date"
									value={formatDateTime(request.confirmedAt ?? new Date().toISOString())}
									helperText="Recorded by us, with your IP address, at the moment you confirm."
								/>
							</Grid>
						</Grid>
					</Stack>
				</Card>
			) : phase === "settled" ? (
				<Alert severity="success">
					Confirmed{request.confirmedByName ? ` by ${request.confirmedByName}` : ""}
					{request.confirmedAt ? ` on ${formatDateTime(request.confirmedAt)}` : ""}.
				</Alert>
			) : null}

			<WizardFooter
				busy={busy || assembling || pricingNow}
				continueLabel={continueLabel}
				// The guarantee, unchanged and load-bearing: the confirm is live only in `confirm`,
				// which exists only once `currentQuote` is rendered above. A client cannot sign a
				// quote they have not seen, because until it is on the screen there is no signature
				// to press.
				continueDisabled={readOnly || (phase === "confirm" ? !canSign : !(phase === "failed" || priceAction))}
				onBack={() => goTo(7)}
				onContinue={() => void (phase === "confirm" ? handleConfirm() : priceAction ? priceNow() : runQuote())}
			/>
		</Stack>
	);
}
